import { useEffect, useMemo, useRef, useState } from 'react'
import { signIn } from '../auth/cognitoAuth'
import { clearSession, loadSession, storeSession } from '../auth/session'
import {
  claimInvite,
  createClaimAccount,
  lookupClaim,
  myApiBase,
  MyApiError,
  type ClaimLookup,
  type ClaimResult,
} from '../data/myApi'
import { Brand, Button, Card, StatusBadge } from '../components/ui'

/*
 * /claim/<inviteToken> — the invite-email landing (OnePulse slice 1, reusing
 * the PR #4 claim-flow intent: this IS the C1/C2 claim path, now that its
 * backend exists in services/my-api).
 *
 * Signed out: show who invited you to what, then hosted-UI sign-in/sign-up
 * (the token is stashed for the OAuth round trip). Signed in: claim via the
 * API — authorisation is the VERIFIED EMAIL MATCH server-side; the URL token
 * is only a lookup key, so a forwarded link cannot take the ticket.
 */

type Phase =
  | { kind: 'loading' }
  | { kind: 'unconfigured' }
  | { kind: 'lookup_failed'; status: number }
  | { kind: 'landing'; lookup: ClaimLookup }
  | { kind: 'claiming'; lookup: ClaimLookup }
  | { kind: 'claimed'; result: ClaimResult }
  | { kind: 'claim_failed'; lookup: ClaimLookup; code: string }

export function ClaimLanding({ token }: { token: string }) {
  const session = useMemo(() => loadSession(), [])
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' })
  const [busy, setBusy] = useState(false)
  const [password, setPassword] = useState('')
  const [authErr, setAuthErr] = useState<string | null>(null)
  const claimFired = useRef(false)

  useEffect(() => {
    const base = myApiBase()
    if (!base) {
      setPhase({ kind: 'unconfigured' })
      return
    }
    let cancelled = false
    lookupClaim(base, token)
      .then((lookup) => {
        if (cancelled) return
        setPhase({ kind: 'landing', lookup })
      })
      .catch((e) => {
        if (cancelled) return
        setPhase({ kind: 'lookup_failed', status: e instanceof MyApiError ? e.status : 0 })
      })
    return () => {
      cancelled = true
    }
  }, [token])

  // Signed in + invite still open -> claim exactly once.
  useEffect(() => {
    const base = myApiBase()
    if (!base || !session || claimFired.current) return
    if (phase.kind !== 'landing' || phase.lookup.state !== 'invited') return
    claimFired.current = true
    setPhase({ kind: 'claiming', lookup: phase.lookup })
    claimInvite(session.idToken, base, token)
      .then((result) => setPhase({ kind: 'claimed', result }))
      .catch((e) =>
        setPhase({
          kind: 'claim_failed',
          lookup: phase.lookup,
          code: e instanceof MyApiError && e.code ? e.code : 'unknown',
        }),
      )
  }, [phase, session, token])

  // Single smart-path (BOARDROOM-222954): NO self-declared create-vs-signin. The
  // server (my-api, Cognito-admin) detects whether the invited email already has
  // an account and either CREATES it with this password (new invitee) or reports
  // it exists; either way we then SRP sign-in with the entered password and the
  // claim effect below fires. A new invitee never lands on a failing sign-in.
  const onSetPassword = async () => {
    const base = myApiBase()
    if (!base) return
    if (password.length < 8) { setAuthErr('Choose a password of at least 8 characters.'); return }
    setBusy(true)
    setAuthErr(null)
    try {
      const { email } = await createClaimAccount(base, token, password)
      const result = await signIn(email, password)
      if (result.status !== 'ok') {
        setAuthErr('Unexpected sign-in step — please try again.')
        setBusy(false)
        return
      }
      storeSession(result.tokens)
      // Remount with a session → the claim effect claims the ticket + routes to
      // get-ready (mirrors the branded-login DONE = storeSession + reload).
      window.location.reload()
    } catch {
      // A create/network failure, OR (existing account) a wrong password.
      setAuthErr(
        'That didn’t work. If you already have an account for this invite, enter your existing password. Otherwise try again shortly.',
      )
      setBusy(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 20 }}>
      <div className="col" style={{ width: 400, gap: 18 }}>
        <div className="col" style={{ alignItems: 'center', gap: 10 }}>
          <Brand size={30} />
          <h1 style={{ fontSize: 20 }}>My Trumin</h1>
        </div>

        {phase.kind === 'loading' && (
          <p className="secondary" role="status" style={{ fontSize: 13, textAlign: 'center' }}>Looking up your invite…</p>
        )}

        {phase.kind === 'unconfigured' && (
          <p className="faint" data-wired="false" role="status" style={{ fontSize: 12, textAlign: 'center', color: 'var(--warn)' }}>
            The claim service is not connected in this build — your invite is safe;
            claiming works the moment the service is live. Nothing here is fabricated.
          </p>
        )}

        {phase.kind === 'lookup_failed' && (
          <p role="alert" className="secondary" style={{ fontSize: 13, textAlign: 'center' }}>
            {phase.status === 404
              ? 'This invite link is not recognised — it may have been revoked and re-sent. Check the newest email, or ask your purchaser to re-invite you.'
              : 'The claim service is not responding right now — try this link again shortly.'}
          </p>
        )}

        {(phase.kind === 'landing' || phase.kind === 'claiming' || phase.kind === 'claim_failed') && (
          <InviteCard lookup={phase.kind === 'landing' ? phase.lookup : phase.kind === 'claiming' ? phase.lookup : phase.lookup} />
        )}

        {phase.kind === 'landing' && phase.lookup.state !== 'invited' && (
          <p className="secondary" role="status" style={{ fontSize: 13, textAlign: 'center' }}>
            {phase.lookup.state === 'revoked'
              ? 'This invite was revoked by the purchaser — ask them to re-invite you if that is unexpected.'
              : 'This ticket has already been claimed. Sign in with the invited email to see it.'}
          </p>
        )}

        {phase.kind === 'landing' && phase.lookup.state === 'invited' && !session && (
          <div className="col" style={{ gap: 10 }}>
            <p className="secondary" style={{ fontSize: 13, textAlign: 'center' }}>
              Set a password for <strong>{phase.lookup.allocatedEmailMasked}</strong> to claim your ticket and get ready.
            </p>
            <input
              data-testid="claim-password"
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); if (authErr) setAuthErr(null) }}
              onKeyDown={(e) => { if (e.key === 'Enter') void onSetPassword() }}
              placeholder="Password (at least 8 characters)"
              autoComplete="current-password"
              aria-label="Password"
              disabled={busy}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '12px 14px', fontSize: 15,
                borderRadius: 10, border: '1px solid var(--hairline-16)', background: 'var(--surface)',
                color: 'var(--text)', fontFamily: 'inherit',
              }}
            />
            {authErr && (
              <p data-testid="claim-auth-error" role="alert" className="secondary" style={{ fontSize: 12, color: 'var(--warn)', textAlign: 'center' }}>
                {authErr}
              </p>
            )}
            <Button variant="primary" full onClick={() => void onSetPassword()} disabled={busy || password.length < 8}>
              {busy ? 'Setting up…' : 'Set password & get ready'}
            </Button>
            <p className="faint" style={{ fontSize: 11, textAlign: 'center' }}>
              New here? This creates your account. Already have one? Enter your existing password.
              Your ticket is tied to this email.
            </p>
          </div>
        )}

        {phase.kind === 'claiming' && (
          <p className="secondary" role="status" style={{ fontSize: 13, textAlign: 'center' }}>Claiming your ticket…</p>
        )}

        {phase.kind === 'claimed' && (
          <div className="col" style={{ gap: 12, textAlign: 'center' }}>
            <StatusBadge tone="success" text="TICKET CLAIMED" />
            <p className="secondary" style={{ fontSize: 13 }}>
              {phase.result.ticketTypeName} · {phase.result.eventName} is yours.
              Next: get ready — complete your profile, waiver and heat before your
              entry QR can be issued.
            </p>
            <Button variant="primary" full onClick={() => window.location.replace('/')}>
              Open My Trumin & get ready
            </Button>
          </div>
        )}

        {phase.kind === 'claim_failed' && (
          <div className="col" style={{ gap: 10, textAlign: 'center' }}>
            <p role="alert" className="secondary" style={{ fontSize: 13 }}>
              {phase.code === 'email_mismatch'
                ? `This invite belongs to ${phase.lookup.allocatedEmailMasked}, which doesn't match the account you're signed in with.`
                : phase.code === 'not_claimable'
                  ? 'This invite is no longer open — it may already be claimed or revoked.'
                  : 'Claiming failed — the service returned an error. Try again shortly.'}
            </p>
            {phase.code === 'email_mismatch' && (
              <Button variant="outline" full onClick={() => { clearSession(); window.location.reload() }}>
                Switch account
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function InviteCard({ lookup }: { lookup: ClaimLookup }) {
  return (
    <Card>
      <div className="col" style={{ padding: 18, gap: 6, textAlign: 'center' }}>
        <span className="eyebrow">You&rsquo;re invited</span>
        <h2 style={{ fontSize: 18 }}>{lookup.eventName}</h2>
        <span className="secondary" style={{ fontSize: 12 }}>
          {lookup.ticketTypeName}
          {lookup.venue ? ` · ${lookup.venue}` : ''}
        </span>
        <p className="secondary" style={{ fontSize: 13, marginTop: 6 }}>
          {lookup.invitedByName ?? 'Your purchaser'} allocated you a ticket —
          claim it and get ready.
        </p>
      </div>
    </Card>
  )
}
